import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { MapPin, Clock, User } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookAppointmentPage() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState({ morning: [], afternoon: [], evening: [] });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    patient_name: '',
    patient_email: '',
    patient_phone: '',
    appointment_type: 'new_patient',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async (date) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/appointments/available/${date}`);
      setAvailableSlots(response.data);
    } catch (error) {
      toast.error('Failed to fetch available time slots');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      toast.error('Please select a date and time');
      return;
    }

    if (!formData.patient_name.trim()) {
      toast.error('Please enter your full name');
      return;
    }

    if (!formData.patient_phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, {
        ...formData,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime
      });
      toast.success('Appointment booked successfully! We will confirm via email or WhatsApp.');
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book appointment');
    } finally {
      setSubmitting(false);
    }
  };

  const disabledDays = [
    { before: new Date() },
    { dayOfWeek: [0] }
  ];

  const TimeSlotSection = ({ title, slots }) => (
    <div className="mb-4">
      <h4 className="text-sm font-medium text-slate-500 mb-2">{title}</h4>
      <div className="flex flex-wrap gap-2">
        {slots.length === 0 ? (
          <p className="text-sm text-slate-400">No slots available</p>
        ) : (
          slots.map((slot) => {
            const time = typeof slot === 'string' ? slot : slot.time;
            const status = typeof slot === 'string' ? 'available' : slot.status;
            const isUnavailable = status === 'booked' || status === 'blocked';
            
            return (
              <button
                key={time}
                type="button"
                onClick={() => !isUnavailable && setSelectedTime(time)}
                disabled={isUnavailable}
                className={`time-slot ${selectedTime === time ? 'selected' : ''} ${isUnavailable ? 'unavailable' : ''}`}
                data-testid={`time-slot-${time.replace(':', '')}`}
                title={isUnavailable ? 'This slot is unavailable' : 'Available'}
              >
                {time}
              </button>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-8 bg-[#5ce1e6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden">
            <img src="/images/Logo4.png" alt="BN Physiotherapy" className="w-full h-full object-cover" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Book an Appointment
          </h1>
          <p className="text-slate-800 mb-2">
            Book an appointment at the practice.
          </p>
          <p className="text-slate-800">
            Please call, email or send a WhatsApp message to book a home visit
          </p>
          <Link to="/pricing" className="text-slate-900 font-medium hover:underline mt-2 inline-block" data-testid="pricing-link">
            See price breakdown here
          </Link>
        </div>
      </section>

      {/* Separator */}
      <div className="border-t-4 border-slate-400 w-full"></div>

      {step === 3 ? (
        <section className="py-16 bg-white">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="bg-green-50 rounded-2xl p-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Appointment Booked!</h2>
              <p className="text-slate-600 mb-6">
                Your appointment on {selectedDate && format(selectedDate, 'MMMM d, yyyy')} at {selectedTime} has been confirmed.
              </p>
              <Link to="/" className="inline-flex items-center justify-center px-6 py-2 bg-[#5ce1e6] text-slate-900 rounded-full font-medium hover:bg-[#22d3ee]">
                Return Home
              </Link>
            </div>
          </div>
        </section>
      ) : (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                {/* Appointment Type */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">Appointment Type</h3>
                  <RadioGroup
                    value={formData.appointment_type}
                    onValueChange={(value) => setFormData({ ...formData, appointment_type: value })}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="new_patient" id="new_patient" data-testid="new-patient-radio" />
                      <Label htmlFor="new_patient">New Patient</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="follow_up" id="follow_up" data-testid="follow-up-radio" />
                      <Label htmlFor="follow_up">Follow Up</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Calendar */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">Select a Date</h3>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={disabledDays}
                    className="rounded-md"
                    data-testid="booking-calendar"
                  />
                </div>

                {/* Time Slots */}
                {selectedDate && (
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6 animate-fade-in">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">
                      Available Times for {format(selectedDate, 'MMMM d, yyyy')}
                    </h3>
                    {loading ? (
                      <p className="text-slate-500">Loading available slots...</p>
                    ) : (
                      <>
                        <TimeSlotSection title="Morning" slots={availableSlots.morning} />
                        <TimeSlotSection title="Afternoon" slots={availableSlots.afternoon} />
                        <TimeSlotSection title="Evening" slots={availableSlots.evening} />
                      </>
                    )}
                  </div>
                )}

                {/* Patient Details */}
                {selectedDate && selectedTime && (
                  <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 animate-fade-in">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">Your Details</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Full Name <span className="text-red-500">*</span></Label>
                        <Input
                          value={formData.patient_name}
                          onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                          placeholder="Enter your full name"
                          required
                          data-testid="patient-name-input"
                        />
                      </div>
                      <div>
                        <Label>Phone <span className="text-red-500">*</span></Label>
                        <Input
                          value={formData.patient_phone}
                          onChange={(e) => setFormData({ ...formData, patient_phone: e.target.value })}
                          placeholder="Your phone number"
                          required
                          data-testid="patient-phone-input"
                        />
                      </div>
                      <div>
                        <Label>Email (Optional)</Label>
                        <Input
                          type="email"
                          value={formData.patient_email}
                          onChange={(e) => setFormData({ ...formData, patient_email: e.target.value })}
                          placeholder="your@email.com"
                          data-testid="patient-email-input"
                        />
                      </div>
                      <div>
                        <Label>Notes (Optional)</Label>
                        <Textarea
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          placeholder="Any additional information..."
                          rows={3}
                          data-testid="patient-notes-input"
                        />
                      </div>
                      <Button 
                        type="submit" 
                        className="w-full bg-[#5ce1e6] text-slate-900 hover:bg-[#22d3ee]"
                        disabled={submitting}
                        data-testid="book-appointment-btn"
                      >
                        {submitting ? 'Booking...' : 'Book Appointment'}
                      </Button>
                    </div>
                  </form>
                )}
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-24 space-y-6">
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-[#0e7490] flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-slate-800">Location</h4>
                        <p className="text-sm text-slate-600">KT Health & Wellness Medical Center</p>
                        <p className="text-sm text-slate-600">Upper level, Cedar Square, Fourways</p>
                      </div>
                    </div>
                  </div>

                  {selectedDate && (
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 animate-fade-in">
                      <div className="flex items-start gap-3 mb-4">
                        <Clock className="w-5 h-5 text-[#0e7490] flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-slate-800">Date & Time</h4>
                          <p className="text-sm text-slate-600">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</p>
                          {selectedTime && (
                            <p className="text-sm font-medium text-[#0e7490]">{selectedTime}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                    <div className="flex items-center gap-4">
                      <div className="headshot-circle w-16 h-16">
                        <img src="/images/Headshot.jpg" alt="Bongekile" className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-800">With Bongekile</h4>
                        <p className="text-sm text-slate-500">Lead Physiotherapist</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
